import { useTranslation } from "react-i18next";

import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { Link } from "src/components";
import { Alert, Button, Checkbox, Modal } from "src/components-v2";

import { PROHIBITED_ACTIVITIES_EN, PROHIBITED_ACTIVITIES_ID } from "src/consts/links";
import { generateTNCPath } from "src/helpers/link";
import { SERVICE_TYPE_VALUES } from "src/scenes/NewVerification/consts/options";

type AgreementLetterModalProps = {
  /**
   * Show or hide modal
   */
  showLetter: boolean;

  /**
   * Callback for toggling the Modal
   */
  toggleLetter: (_arg: boolean) => void;

  /**
   * Service type from verification-info response
   */
  serviceType: {};

  /**
   * Email that used to sent template
   */
  emailSent: string;

  /**
   * Value of Checkbox component for agreeing the letter
   */
  checked: boolean;

  /**
   * Callback that triggered after changing the Checkbox
   */
  toggleCheckbox: () => void;

  /**
   * Boolean value showing on going submit verification
   */
  isSubmittingVerification: boolean;

  /**
   * Boolean value showing on going submit request change
   */
  isSubmittingRequestChange: boolean;

  /**
   * Boolean value for hiding the request change button
   */
  hideRequestChangeButton: boolean;

  /**
   * Boolean value for for checking if it's business type or personal type
   */
  isBusinessOrPersonal: boolean;

  /**
   * Function for 'I AGREE' button
   */
  submitVerification: () => void;

  /**
   * Function for 'PURPOSE CHANGE' button
   */
  submitRequestChange: () => void;
};

function AgreementLetterModal(props: AgreementLetterModalProps) {
  const {
    emailSent,
    serviceType,
    showLetter,
    checked,
    toggleLetter,
    toggleCheckbox,
    isSubmittingVerification,
    isSubmittingRequestChange,
    submitVerification,
    submitRequestChange,
    hideRequestChangeButton,
    isBusinessOrPersonal
  } = props;
  const { t, i18n } = useTranslation("verification");

  const LINK_PROHIBITED_ACTIVITIES = i18n.language === "id" ? PROHIBITED_ACTIVITIES_ID : PROHIBITED_ACTIVITIES_EN;

  const SELECTED_SERVICE_TYPE = serviceType ? Object.keys(serviceType).map((k) => +k) : [];

  return (
    <Modal
      closeOnOverlay
      title={t("letter-of-agreement.title")}
      show={showLetter}
      onClose={() => toggleLetter(false)}
      size="large"
      data-qaid="qa-verification-summary-agreement-modal"
    >
      <div className="u-text-semi">
        <p>{t("letter-of-agreement.general")}</p>
        <p className="u-font-bold">{t("letter-of-agreement.responsibility")}</p>
        <ul className="u-pl-3 u-pb-3">
          <li className="u-mb-1">
            <span>{t("letter-of-agreement.point-1-1")}</span>
            <Link
              target="_blank"
              to={generateTNCPath()}
              hash="#tnc"
              rel="noopener noreferrer"
              className="u-font-bold u-text-flip-orange u-cursor-pointer"
            >
              {t("letter-of-agreement.point-1-2")}
            </Link>
            <span>{t("letter-of-agreement.point-1-3")}</span>
          </li>
          <li className="u-mb-1">
            <span>{t("letter-of-agreement.point-2-1")}</span>
            <Link
              target="_blank"
              to={LINK_PROHIBITED_ACTIVITIES}
              rel="noopener noreferrer"
              className="u-font-bold u-text-flip-orange u-cursor-pointer"
            >
              {t("letter-of-agreement.point-2-2")}
            </Link>
          </li>
          <li className="u-mb-1">{t("letter-of-agreement.point-3")}</li>

          {SELECTED_SERVICE_TYPE.some((serviceType) =>
            [SERVICE_TYPE_VALUES.MONEY_TRANSFER, SERVICE_TYPE_VALUES.INTERNATIONAL_TRANSFER].includes(serviceType)
          ) && (
            <>
              <li className="u-mb-1">{t("letter-of-agreement.point-4")}</li>
              <li className="u-mb-1">{t("letter-of-agreement.point-5")}</li>
            </>
          )}

          {SELECTED_SERVICE_TYPE.includes(SERVICE_TYPE_VALUES.ACCEPT_PAYMENT) && !isBusinessOrPersonal && (
            <li className="u-mb-1">{t("letter-of-agreement.point-6")}</li>
          )}

          <li>{t("letter-of-agreement.point-7")}</li>
        </ul>

        {!hideRequestChangeButton && (
          <div className="u-mb-5">
            <p className="u-font-bold u-mb-0">{t("letter-of-agreement.change-request-title")}</p>
            <p>{t("letter-of-agreement.change-request-desc")}</p>
          </div>
        )}

        <Alert color="blue" icon={faInfoCircle}>
          {t("letter-of-agreement.change-request-info")}
          <strong>{emailSent}</strong>
        </Alert>
      </div>
      <div className="u-flex u-items-start u-justify-between u-px-3 u-py-8">
        <Checkbox
          checked={checked}
          onChange={toggleCheckbox}
          data-qaid="qa-verification-summary-agreement-modal-tnc-checkbox"
        />
        <span>{t("letter-of-agreement.agreed-desc")}</span>
      </div>
      <Button
        disabled={!checked || isSubmittingVerification || isSubmittingRequestChange}
        activated={isSubmittingVerification}
        className="u-mb-3"
        onClick={submitVerification}
        block
        data-qaid="qa-verification-summary-agreement-modal-agree-button"
      >
        {t("letter-of-agreement.agree")}
      </Button>
      {!hideRequestChangeButton && (
        <Button
          outline
          activated={isSubmittingRequestChange}
          disabled={isSubmittingRequestChange || isSubmittingVerification}
          onClick={submitRequestChange}
          block
          data-qaid="qa-verification-summary-agreement-modal-propose-change-button"
        >
          {t("letter-of-agreement.request-change")}
        </Button>
      )}
    </Modal>
  );
}

export default AgreementLetterModal;
